import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { FileText, Save, Plus, Edit, Trash2, Eye, EyeOff, BookOpen, Info, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';

const ContentManagement = () => {
  const [activeSubTab, setActiveSubTab] = useState('about');
  const [aboutContent, setAboutContent] = useState({ title: '', content: '' });
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: '',
    is_published: false,
    seo_title: '',
    seo_description: ''
  });

  useEffect(() => {
    if (activeSubTab === 'about') {
      fetchAboutContent();
    } else {
      fetchBlogPosts();
    }
  }, [activeSubTab]);

  const fetchAboutContent = async () => {
    try {
      const response = await axios.get(`${API}/page-content/about`);
      setAboutContent(response.data || { title: 'Our Story', content: '' });
    } catch (error) {
      setAboutContent({ title: 'Our Story', content: '' });
    } finally {
      setLoading(false);
    }
  };

  const fetchBlogPosts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/blog-posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAbout = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/page-content/about`, aboutContent, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('About page updated!');
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    
    // Auto-generate slug if empty
    const submitData = {
      ...blogForm,
      slug: blogForm.slug || blogForm.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    };
    
    try {
      if (editingPost) {
        await axios.put(`${API}/admin/blog-posts/${editingPost.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Blog post updated');
      } else {
        await axios.post(`${API}/admin/blog-posts`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Blog post created');
      }
      setShowBlogForm(false);
      setEditingPost(null);
      resetBlogForm();
      fetchBlogPosts();
    } catch (error) {
      toast.error('Failed to save blog post');
    }
  };

  const resetBlogForm = () => {
    setBlogForm({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      featured_image: '',
      is_published: false,
      seo_title: '',
      seo_description: ''
    });
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setBlogForm(post);
    setShowBlogForm(true);
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/blog-posts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Blog post deleted');
      fetchBlogPosts();
    } catch (error) {
      toast.error('Failed to delete blog post');
    }
  };

  const togglePublish = async (post) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`${API}/admin/blog-posts/${post.id}/toggle-publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBlogPosts();
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  return (
    <div data-testid="content-management">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Content Management</h1>
          <p className="text-[#a8b8d0] text-sm mt-1">Manage About Us and Blog pages</p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setActiveSubTab('about')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            activeSubTab === 'about'
              ? 'bg-[#1a4d8c] text-white'
              : 'bg-[#132238] text-[#a8b8d0] hover:text-white'
          }`}
        >
          <Info size={18} />
          About Us
        </button>
        <button
          onClick={() => setActiveSubTab('blog')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            activeSubTab === 'blog'
              ? 'bg-[#1a4d8c] text-white'
              : 'bg-[#132238] text-[#a8b8d0] hover:text-white'
          }`}
        >
          <BookOpen size={18} />
          Blog Posts
        </button>
      </div>

      {/* About Us Editor */}
      {activeSubTab === 'about' && (
        <div className="bg-[#132238] rounded-xl p-6 border border-[#1a2d47]">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <FileText size={20} className="text-[#ff6b35]" />
              About Us Page Content
            </h2>
            <button onClick={handleSaveAbout} disabled={saving}
              className="btn-accent inline-flex items-center gap-2 disabled:opacity-50">
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="text-[#a8b8d0] text-sm mb-1 block">Page Title</label>
              <Input
                value={aboutContent.title}
                onChange={(e) => setAboutContent({ ...aboutContent, title: e.target.value })}
                placeholder="Our Story"
                className="bg-[#1a2d47] border-[#1a4d8c] text-white"
              />
            </div>
            <div>
              <label className="text-[#a8b8d0] text-sm mb-1 block">Page Content</label>
              <textarea
                value={aboutContent.content}
                onChange={(e) => setAboutContent({ ...aboutContent, content: e.target.value })}
                placeholder="Write your About Us content here..."
                rows={12}
                className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-3 text-white placeholder-[#a8b8d0]"
              />
              <p className="text-[#a8b8d0] text-xs mt-1">Use line breaks for paragraphs.</p>
            </div>
          </div>
        </div>
      )}

      {/* Blog Posts Manager */}
      {activeSubTab === 'blog' && (
        <>
          <div className="flex justify-end mb-4">
            <button
              onClick={() => { resetBlogForm(); setEditingPost(null); setShowBlogForm(true); }}
              className="btn-accent inline-flex items-center gap-2"
            >
              <Plus size={20} />
              New Blog Post
            </button>
          </div>

          {/* Blog Form Modal */}
          {showBlogForm && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-[#132238] rounded-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-white">
                    {editingPost ? 'Edit Blog Post' : 'New Blog Post'}
                  </h2>
                  <button onClick={() => { setShowBlogForm(false); setEditingPost(null); }}
                    className="text-[#a8b8d0] hover:text-white">
                    <X size={24} />
                  </button>
                </div>
                
                <form onSubmit={handleBlogSubmit} className="space-y-4">
                  <div>
                    <label className="text-[#a8b8d0] text-sm mb-1 block">Title *</label>
                    <Input
                      value={blogForm.title}
                      onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
                      required
                      placeholder="Blog Post Title"
                      className="bg-[#1a2d47] border-[#1a4d8c] text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="text-[#a8b8d0] text-sm mb-1 block">URL Slug (auto-generated if empty)</label>
                    <Input
                      value={blogForm.slug}
                      onChange={(e) => setBlogForm({ ...blogForm, slug: e.target.value })}
                      placeholder="blog-post-title"
                      className="bg-[#1a2d47] border-[#1a4d8c] text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="text-[#a8b8d0] text-sm mb-1 block">Excerpt (Short description)</label>
                    <textarea
                      value={blogForm.excerpt}
                      onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })}
                      placeholder="A brief summary of the post..."
                      rows={2}
                      className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="text-[#a8b8d0] text-sm mb-1 block">Content *</label>
                    <textarea
                      value={blogForm.content}
                      onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })}
                      required
                      placeholder="Write your blog post content here..."
                      rows={10}
                      className="w-full bg-[#1a2d47] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="text-[#a8b8d0] text-sm mb-1 block">Featured Image URL</label>
                    <Input
                      value={blogForm.featured_image}
                      onChange={(e) => setBlogForm({ ...blogForm, featured_image: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                      className="bg-[#1a2d47] border-[#1a4d8c] text-white"
                    />
                  </div>
                  
                  <div className="bg-[#1a2d47]/50 rounded-lg p-4">
                    <h3 className="text-white font-medium mb-3">SEO Settings</h3>
                    <div className="space-y-3">
                      <Input
                        value={blogForm.seo_title}
                        onChange={(e) => setBlogForm({ ...blogForm, seo_title: e.target.value })}
                        placeholder="SEO Title (defaults to post title)"
                        className="bg-[#0a1628] border-[#1a4d8c] text-white"
                      />
                      <textarea
                        value={blogForm.seo_description}
                        onChange={(e) => setBlogForm({ ...blogForm, seo_description: e.target.value })}
                        placeholder="SEO Meta Description"
                        rows={2}
                        className="w-full bg-[#0a1628] border border-[#1a4d8c] rounded-lg px-4 py-2 text-white text-sm"
                      />
                    </div>
                  </div>
                  
                  <label className="flex items-center gap-2 text-white">
                    <Checkbox
                      checked={blogForm.is_published}
                      onCheckedChange={(c) => setBlogForm({ ...blogForm, is_published: c })}
                      className="border-[#1a4d8c] data-[state=checked]:bg-[#22c55e]"
                    />
                    Publish immediately
                  </label>
                  
                  <div className="flex gap-4 justify-end pt-4 border-t border-[#1a2d47]">
                    <button type="button" onClick={() => { setShowBlogForm(false); setEditingPost(null); }}
                      className="px-4 py-2 text-[#a8b8d0] hover:text-white">Cancel</button>
                    <button type="submit" className="btn-accent">
                      {editingPost ? 'Update' : 'Create'} Post
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Blog Posts List */}
          <div className="bg-[#132238] rounded-xl border border-[#1a2d47] overflow-hidden">
            {blogPosts.length > 0 ? (
              <table className="w-full">
                <thead className="bg-[#1a2d47]">
                  <tr>
                    <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm font-medium">Title</th>
                    <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm font-medium">Status</th>
                    <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm font-medium">Date</th>
                    <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blogPosts.map((post) => (
                    <tr key={post.id} className="border-t border-[#1a2d47]">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-white font-medium">{post.title}</p>
                          <p className="text-[#a8b8d0] text-xs">/blog/{post.slug}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          post.is_published
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {post.is_published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#a8b8d0] text-sm">
                        {new Date(post.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => togglePublish(post)}
                            className="p-2 text-[#a8b8d0] hover:text-white hover:bg-[#1a2d47] rounded"
                            title={post.is_published ? 'Unpublish' : 'Publish'}>
                            {post.is_published ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                          <button onClick={() => handleEditPost(post)}
                            className="p-2 text-[#a8b8d0] hover:text-white hover:bg-[#1a2d47] rounded">
                            <Edit size={16} />
                          </button>
                          <button onClick={() => handleDeletePost(post.id)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12">
                <BookOpen size={48} className="text-[#a8b8d0] mx-auto mb-4" />
                <p className="text-[#a8b8d0]">No blog posts yet. Create your first post!</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ContentManagement;
